import React from 'react';
import {
  Pencil, LayoutTemplate, Copy, Bookmark,
  ChevronUp, ChevronDown, Trash2,
} from 'lucide-react';
import { blockLabels } from '../content-editor/blockTypes';

/**
 * SectionControlCard — Iter 150.
 *
 * A clean white 180px floating card pinned to the right edge of a SELECTED
 * section. Replaces the cluttered dark hover pill as the primary action
 * surface when a section is in selection mode.
 *
 * Layout (top → bottom):
 *   ✏️ Edit Section
 *   🖼 Layouts
 *   ───────
 *   Duplicate
 *   Save as Template
 *   Move Up
 *   Move Down
 *   ───────
 *   🗑 Delete
 */
const Row = ({ icon: Icon, label, onClick, testid, danger, disabled }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); if (!disabled) onClick?.(); }}
    disabled={disabled}
    data-testid={testid}
    className={`w-full px-3 py-2 flex items-center gap-2.5 text-[12px] font-medium rounded-md transition-all
      ${danger
        ? 'text-red-600 hover:bg-red-50'
        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}
      ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <Icon size={13} className="shrink-0" />
    <span className="text-left flex-1">{label}</span>
  </button>
);

const Divider = () => <div className="my-1 mx-2 h-px bg-gray-200" />;

export default function SectionControlCard({
  block,
  onEdit,
  onViewLayouts,
  onDuplicate,
  onSaveTemplate,
  onMoveUp,
  onMoveDown,
  onDelete,
  isFirst,
  isLast,
}) {
  return (
    <div
      className="absolute top-3 right-3 z-30 w-[180px] bg-white rounded-xl border border-gray-200 shadow-[0_12px_40px_-12px_rgba(15,23,42,0.25)] py-1.5 select-none"
      data-testid={`section-control-card-${block.id}`}
      data-wb-scc="1"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Tiny header pill — context label */}
      <div className="px-3 pt-1.5 pb-1 text-[9px] uppercase tracking-[0.16em] font-bold text-gray-400">
        {blockLabels[block.type] || block.type}
      </div>

      <Row icon={Pencil}         label="Edit Section"     onClick={onEdit}        testid={`scc-edit-${block.id}`} />
      <Row icon={LayoutTemplate} label="Layouts"          onClick={onViewLayouts} testid={`scc-layouts-${block.id}`} />
      <Divider />
      <Row icon={Copy}           label="Duplicate"        onClick={onDuplicate}     testid={`scc-duplicate-${block.id}`} />
      <Row icon={Bookmark}       label="Save as Template" onClick={onSaveTemplate}  testid={`scc-save-${block.id}`} />
      <Row icon={ChevronUp}      label="Move Up"          onClick={onMoveUp}        testid={`scc-up-${block.id}`}   disabled={isFirst} />
      <Row icon={ChevronDown}    label="Move Down"        onClick={onMoveDown}      testid={`scc-down-${block.id}`} disabled={isLast} />
      <Divider />
      <Row icon={Trash2}         label="Delete"           onClick={onDelete}        testid={`scc-delete-${block.id}`} danger />
    </div>
  );
}
